{
  "title": "Settings",
  "description": "Your profile, language, security, notifications and company details.",
  "tabs": {
    "profile": "Profile",
    "security": "Security",
    "notifications": "Notifications",
    "organization": "Organisation"
  },
  "profile": {
    "title": "Profile",
    "description": "Your name, panel language and time zone.",
    "name": "Name",
    "language": "Language",
    "languageHint": "The panel's language and the emails we send you.",
    "timeZone": "Time zone",
    "timeZoneHint": "Used for times in the panel, emails and exports.",
    "saved": "Profile saved",
    "zones": {
      "riyadh": "Riyadh",
      "dubai": "Dubai",
      "kuwait": "Kuwait",
      "qatar": "Doha",
      "bahrain": "Manama",
      "muscat": "Muscat",
      "cairo": "Cairo",
      "london": "London",
      "utc": "Coordinated universal time"
    },
    "email": {
      "title": "Email",
      "description": "You sign in with it, and our emails go here.",
      "change": "Change email",
      "verified": "Verified",
      "pendingTitle": "Waiting for confirmation",
      "pendingBody": "We sent a confirmation link to {{email}}. Your current email keeps working until you click it.",
      "demoOpen": "Open confirmation link",
      "cancel": "Cancel change",
      "dialogTitle": "Change email",
      "dialogDescription": "We send a confirmation link to the new address. Nothing changes until you confirm.",
      "new": "New email",
      "password": "Current password",
      "passwordHint": "To make sure it's you.",
      "send": "Send confirmation link",
      "sent": "Confirmation link sent to the new address",
      "confirmed": "Your email was changed"
    }
  },
  "security": {
    "password": {
      "title": "Password",
      "description": "Use a long password you don't use anywhere else.",
      "lastChanged": "Last changed {{when}}.",
      "current": "Current password",
      "new": "New password",
      "confirm": "Confirm new password",
      "submit": "Change password",
      "othersSignedOut": "Changing it signs you out on every other device.",
      "changed": "Password changed",
      "changedBody": "We signed you out on your other devices."
    },
    "twoFactor": {
      "title": "Two-factor authentication",
      "description": "A code from an authenticator app, asked for with your password when you sign in.",
      "on": "On",
      "off": "Off",
      "onBody": "Your account is protected. We ask for an app code whenever you sign in on a new device.",
      "offBody": "Turn it on to protect your servers and invoices if your password leaks.",
      "codesLeft_zero": "You have no recovery codes left. Generate new ones.",
      "codesLeft_one": "1 recovery code left.",
      "codesLeft_other": "{{count}} recovery codes left.",
      "enable": "Turn on two-factor authentication",
      "disable": "Turn off two-factor authentication",
      "regenerate": "Generate new recovery codes",
      "enabled": "Two-factor authentication is on",
      "disabled": "Two-factor authentication is off",
      "requiredByOrg": "Your organisation requires two-factor authentication for every member, so you can't turn it off.",
      "requiredByOrgOff": "Your organisation requires two-factor authentication for every member. Turn it on now, or we'll ask at your next sign-in.",
      "disableTitle": "Turn off two-factor authentication",
      "disableDescription": "After this, your password alone signs you in. Enter your password to confirm.",
      "regenerateTitle": "Generate new recovery codes",
      "regenerateDescription": "Your old codes stop working right away. Enter your password to confirm.",
      "setup": {
        "title": "Turn on two-factor authentication",
        "description": "You need an authenticator app such as Google Authenticator or 1Password.",
        "step1": "Scan the code with your authenticator app, or enter the key manually.",
        "step2": "Enter the 6 digit code the app shows.",
        "qr": "QR code to add the account to your authenticator app",
        "manual": "Or enter this key manually",
        "code": "Verification code",
        "demoCode": "In the demo, use {{code}}",
        "wrongCode": "The code is wrong or expired",
        "verify": "Verify and turn on"
      },
      "codes": {
        "title": "Save your recovery codes",
        "description": "If you lose your phone, sign in with one of these. Each code works once and we won't show them again, so keep them somewhere safe.",
        "newTitle": "New recovery codes",
        "newDescription": "Your old codes stopped working. Keep these somewhere safe.",
        "download": "Download codes",
        "copy": "Copy codes",
        "copied": "Codes copied",
        "saved": "I saved the codes somewhere safe"
      },
      "gate": {
        "title": "Turn on two-factor authentication to continue",
        "body": "Your organisation requires two-factor authentication for every member. Turn it on here, then carry on."
      }
    },
    "sessions": {
      "title": "Signed-in devices",
      "description": "Devices that are signed in to your account right now.",
      "device": "{{browser}} on {{os}}",
      "current": "This device",
      "activeNow": "Active now",
      "lastActive": "Last active {{when}}",
      "signedIn": "Signed in {{when}}",
      "signOut": "Sign out",
      "signOutOthers": "Sign out other devices",
      "confirmOneTitle": "Sign out this device?",
      "confirmOneDescription": "We'll sign out {{device}}. It can sign in again with the password.",
      "confirmAllTitle": "Sign out other devices?",
      "confirmAllDescription_zero": "There are no other devices.",
      "confirmAllDescription_one": "We'll sign out 1 device. This device stays signed in.",
      "confirmAllDescription_other": "We'll sign out {{count}} devices. This device stays signed in.",
      "revoked": "Device signed out",
      "revokedAll": "Other devices signed out"
    },
    "activity": {
      "title": "Sign-in activity",
      "description": "Sign-ins and security changes in the last 30 days.",
      "empty": "No activity in the last 30 days",
      "failures_zero": "No failed attempts.",
      "failures_one": "There was 1 failed sign-in attempt. If it wasn't you, change your password.",
      "failures_other": "There were {{count}} failed sign-in attempts. If they weren't you, change your password.",
      "kinds": {
        "sign_in": "Signed in",
        "sign_in_failed": "Failed sign-in",
        "two_factor_failed": "Wrong authentication code",
        "password_changed": "Password changed",
        "two_factor_enabled": "Two-factor authentication turned on",
        "two_factor_disabled": "Two-factor authentication turned off",
        "recovery_codes_generated": "Recovery codes generated",
        "email_changed": "Email changed",
        "sessions_revoked": "Devices signed out"
      }
    }
  },
  "notifications": {
    "title": "Notifications",
    "description": "Choose what reaches your email and what shows in the panel. Changes save right away.",
    "event": "Event",
    "channels": {
      "email": "Email",
      "inApp": "Panel"
    },
    "groups": {
      "billing": "Billing",
      "servers": "Servers",
      "updates": "Updates",
      "ai": "AI credits",
      "team": "Team",
      "support": "Support",
      "security": "Security"
    },
    "events": {
      "payment_failed": "Payment failed",
      "invoice_paid": "Invoice paid",
      "trial_ending": "Trial ending soon",
      "credits_low": "Credits running low",
      "server_activated": "Server activated",
      "server_grace": "Server can't reach bPanel",
      "update_available": "Update available for a server",
      "release_published": "New bPanel release",
      "member_joined": "Member joined the team",
      "ticket_reply": "Reply on a ticket",
      "ticket_resolved": "Ticket resolved",
      "security_sign_in": "Sign-in from a new device",
      "credits_exhausted": "Credits used up",
      "credits_topped_up": "Credits topped up automatically",
      "server_past_due": "Server renewal waiting for payment",
      "server_locked": "Server control panel locked",
      "server_unlocked": "Server control panel unlocked"
    },
    "locked": "This email is required for your role and can't be turned off",
    "lockedShort": "Email required",
    "billingEmail": "Invoice emails also go to {{email}}.",
    "billingEmailLink": "Edit billing details",
    "saved": "Change saved"
  },
  "organization": {
    "title": "Organisation details",
    "description": "The name and country show to your team and on invoices.",
    "readOnly": "Only the owner and admins can edit organisation details.",
    "name": "Organisation name",
    "nameHint": "The legal name is edited in billing details.",
    "country": "Country",
    "billingEmail": "Billing emails",
    "billingEmailHint": "Invoices and receipts go here.",
    "billingEmailLink": "Edit it in billing details",
    "saved": "Organisation details saved",
    "logo": {
      "title": "Logo",
      "description": "Shows in the organisation switcher and in invitation emails.",
      "alt": "{{name}} logo",
      "upload": "Upload logo",
      "replace": "Replace logo",
      "remove": "Remove",
      "hint": "PNG, JPG, SVG or WebP, up to 512 KB. Square works best.",
      "type": "Unsupported file type. Use PNG, JPG, SVG or WebP.",
      "size": "The file is larger than 512 KB.",
      "saved": "Logo saved"
    },
    "delete": {
      "title": "Delete organisation",
      "description": "Permanent and can't be undone.",
      "what1": "We delete the team, invitations, activation keys and support tickets.",
      "what2": "We keep paid invoices for as long as the law requires, and you can request a copy.",
      "what3": "Before deleting, cancel every server's subscription and pay anything due.",
      "blockedTitle": "You can't delete the organisation yet",
      "blockedServers_zero": "No active servers.",
      "blockedServers_one": "1 server still has an active subscription.",
      "blockedServers_other": "{{count}} servers still have an active subscription.",
      "toServers": "View servers",
      "blockedBalance": "Amount due:",
      "toBilling": "View billing",
      "ownerOnly": "Only the owner can delete the organisation.",
      "button": "Delete organisation",
      "confirmTitle": "Delete {{name}} permanently?",
      "confirmDescription": "This can't be undone. Type the organisation name to confirm.",
      "confirm": "Delete permanently",
      "done": "{{name}} deleted"
    }
  }
}
